import React, {useState, useRef, useEffect} from "react";
import {useForm} from "react-hook-form";
import {Plus, X, RefreshCw, Check, ChevronLeft, Calendar, Clock} from "lucide-react";
import Button from "../../Common/Button";
import {Link, useLocation, useNavigate} from "react-router-dom";
import ColorPalette from "../ColorPalette";
import IOSSwitch from "../../Common/IOSSwitch";
import {MdClose} from 'react-icons/md';
import secureStorage from "../../../utils/secureStorage";
import {api} from "../../../config/api";
import ReferencePickerModal from "../../Common/ReferencePickerModal";
import {categoriesConfig, brandsConfig, taxesConfig, unitsConfig} from "../../Common/referenceConfigs";
import {generateProductBarcode} from "../../../utils/barcodeGenerator";
import Notification from "../../Common/Notification";

const NewProductForm = () => {
    const navigate = useNavigate();
    // Back to the catalogue tab we came from (Articles, Prestations…), filters kept
    const returnTo = useLocation().state?.returnTo || "/catalogue";
    const [currentColor, setCurrentColor] = useState("#4DB6AC");
    const [showPalette, setShowPalette] = useState(false);
    const [productImage, setProductImage] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [isStockable, setIsStockable] = useState(true);
    const [isVendable, setIsVendable] = useState(true);
    const [isIngredient, setIsIngredient] = useState(false);
    const [purchasePriceHt, setPurchasePriceHt] = useState('');
    const [purchasePriceTtc, setPurchasePriceTtc] = useState('');
    const [isRestaurant, setIsRestaurant] = useState(false);
    // Calendar / online booking (same settings as the product's "Paramètres calendrier"),
    // salons de beauté only (busniessType === "001")
    const [isBeautySalon, setIsBeautySalon] = useState(false);
    const [showOnCalendar, setShowOnCalendar] = useState(false);
    const [duration, setDuration] = useState(30);

    // Check once if this is a restaurant account (busniessType === "004") — the
    // Ingrédients fields (prix d'achat HT/TTC, code interne) only make sense there.
    useEffect(() => {
        secureStorage.getItem('selectedAccount').then(acc => {
            setIsRestaurant(acc?.busniessType === '004');
            setIsBeautySalon(acc?.busniessType === '001');
        });
    }, []);

    // Selected references (store full item for display)
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [selectedTax, setSelectedTax] = useState(null);

    // Picker modal state — which picker is open
    const [activePicker, setActivePicker] = useState(null); // 'category' | 'brand' | 'unit' | 'tax' | null

    const [notification, setNotification] = useState({
        show: false,
        type: 'success',
        message: '',
    });

    const {
        register,
        handleSubmit,
        formState: {errors, isSubmitting},
        setValue,
    } = useForm();

    const formRef = useRef(null);

    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        if (type === 'success') {
            setTimeout(() => setNotification(prev => ({...prev, show: false})), 5000);
        }
    };

    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    const onSubmit = async (data) => {
        try {
            if (!selectedCategory) {
                showNotification('error', "Veuillez sélectionner une catégorie");
                return;
            }
            if (!selectedUnit) {
                showNotification('error', "Veuillez sélectionner une unité");
                return;
            }

            showNotification('loading', "Création du produit en cours...");

            const formData = new FormData();
            formData.append("name", data.name);
            formData.append("color", currentColor);
            formData.append("description", data.description || "");
            formData.append("price", data.price);
            formData.append("barcode", data.barcode || "");
            formData.append("minStock", data.minStock || 0);
            formData.append("category", `/api/categories/${selectedCategory.id}`);
            if (selectedBrand) formData.append("brand", `/api/brands/${selectedBrand.id}`);
            formData.append("units", `/api/units/${selectedUnit.id}`);
            if (selectedTax) formData.append("taxes", `/api/taxes/${selectedTax.id}`);
            formData.append("active", isActive);
            formData.append("isStockable", isStockable);
            formData.append("isVendable", isVendable);
            formData.append("isIngredient", isIngredient);
            formData.append("internalCode", data.internalCode || "");
            formData.append("purchasePriceHt", purchasePriceHt);
            formData.append("purchasePriceTtc", purchasePriceTtc);
            if (isBeautySalon) {
                formData.append("show_on_calendar", String(showOnCalendar));
                formData.append("duration", String(duration));
            }

            if (productImage) {
                if (productImage instanceof File) {
                    formData.append("image", productImage);
                } else if (typeof productImage === 'string' && productImage.startsWith('data:')) {
                    const response = await fetch(productImage);
                    const blob = await response.blob();
                    const file = new File([blob], "product-image.jpg", {type: "image/jpeg"});
                    formData.append("image", file);
                }
            }

            const accountId = await secureStorage.getItem("selectedAccount");
            await api.post('/products', formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Content-Type': undefined,
                    'Accept': 'application/json'
                },
                maxBodyLength: Infinity
            });

            showNotification('success', "Produit créé avec succès");
            setTimeout(() => navigate(returnTo), 1500);
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        }
    };

    const handleColorChange = (color) => { setCurrentColor(color); setShowPalette(false); };
    const handleImageChange = (image) => { setProductImage(image); };
    const togglePalette = () => { setShowPalette(prev => !prev); };
    const toggleActive = () => { setIsActive(prev => !prev); };
    const toggleStockable = () => { setIsStockable(prev => !prev); };
    const toggleVendable = () => { setIsVendable(prev => !prev); };
    const toggleIngredient = () => { setIsIngredient(prev => !prev); };
    const handleDeleteImage = () => { setProductImage(null); };

    // Prix d'achat HT/TTC — dérivation automatique via le taux de TVA sélectionné
    // (ex: 80 DH TTC à 20% → 66,67 DH HT). Si aucune taxe n'est sélectionnée,
    // les deux champs restent en saisie libre indépendante.
    const taxRate = selectedTax ? parseFloat(selectedTax.value) : null;

    const handlePurchaseHtChange = (value) => {
        setPurchasePriceHt(value);
        if (taxRate !== null && !isNaN(taxRate) && value !== '') {
            const ht = parseFloat(value);
            if (!isNaN(ht)) setPurchasePriceTtc((ht * (1 + taxRate / 100)).toFixed(2));
        }
    };

    const handlePurchaseTtcChange = (value) => {
        setPurchasePriceTtc(value);
        if (taxRate !== null && !isNaN(taxRate) && value !== '') {
            const ttc = parseFloat(value);
            if (!isNaN(ttc)) setPurchasePriceHt((ttc / (1 + taxRate / 100)).toFixed(2));
        }
    };

    /* ─── Reference Selector Button component ─── */
    const RefSelector = ({label, required, value, placeholder, onClick, onClear}) => (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            </div>
            <button
                type="button"
                onClick={onClick}
                className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 text-sm border rounded-xl transition-all text-left ${
                    value
                        ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                        : 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50 hover:border-primary/50'
                }`}
            >
                {value ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        {value.color && !value.hideColor && (
                            <span className="w-5 h-5 rounded-md flex-shrink-0" style={{backgroundColor: value.color}}/>
                        )}
                        <span className="text-gray-800 dark:text-white font-medium truncate">
                            {value.displayLabel || value.name || '—'}
                        </span>
                    </div>
                ) : (
                    <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
                )}
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {value && (
                        <span onClick={(e) => { e.stopPropagation(); onClear(); }}
                              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer">
                            <X className="w-3.5 h-3.5"/>
                        </span>
                    )}
                    <Plus className="w-4 h-4 text-gray-400"/>
                </div>
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Sticky header: back · title · actions (main action on the right, filled) */}
            <header className="sticky top-0 z-30 flex items-center gap-2 sm:gap-4 px-3 sm:px-6 h-14 sm:h-16 border-b border-gray-200 dark:border-gray-700 bg-white/85 dark:bg-gray-900/85 backdrop-blur-xl">
                <div className="flex-1 min-w-0">
                    <Button as={Link} to={returnTo} variant="ghost" icon={ChevronLeft} compact className="-ml-2">
                        Retour
                    </Button>
                </div>
                <h1 className="text-base sm:text-lg font-semibold text-center text-gray-800 dark:text-white truncate">
                    Nouveau produit
                </h1>
                <div className="flex-1 min-w-0 flex items-center justify-end gap-1 sm:gap-2">
                    <Button variant="plain" icon={Check} loading={isSubmitting}
                            onClick={() => formRef.current && handleSubmit(onSubmit)()}>
                        Enregistrer
                    </Button>
                </div>
            </header>

            <form
                ref={formRef}
                className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8 w-full"
                onSubmit={handleSubmit(onSubmit)}
            >
                {/* Image/Color section - responsive */}
                <div className="flex flex-col items-center space-y-3">
                    <div className="text-center mb-2">
                        {productImage ? (
                            <div className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto">
                                <img
                                    src={productImage}
                                    alt="Product"
                                    className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg shadow-sm cursor-pointer"
                                    onClick={togglePalette}
                                />
                                <button
                                    type="button"
                                    onClick={handleDeleteImage}
                                    className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-red-500 text-white rounded-full p-1 sm:p-1.5 shadow-sm hover:bg-red-600 transition-colors"
                                    aria-label="Delete image"
                                >
                                    <MdClose className="w-3 h-3 sm:w-4 sm:h-4"/>
                                </button>
                            </div>
                        ) : (
                            <div
                                className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg flex items-center justify-center cursor-pointer shadow-sm hover:shadow-md transition-shadow mx-auto text-white text-xs sm:text-sm"
                                onClick={togglePalette}
                                style={{backgroundColor: currentColor}}
                            >
                                Éditer
                            </div>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">
                            Cliquez pour modifier
                        </span>
                    </div>
                </div>

                {/* Form fields section - responsive */}
                <div className="space-y-4 sm:space-y-6">
                    <div>
                        <label htmlFor="name"
                               className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                            Nom :
                        </label>
                        <input
                            type="text"
                            id="name"
                            autoComplete="off"
                            {...register("name", {
                                required: "Le champ 'Nom' est obligatoire.",
                            })}
                            placeholder="Nom du produit*"
                            className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                errors.name
                                    ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                    : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                            }`}
                        />
                        {errors.name && (
                            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.name.message}</p>
                        )}
                    </div>

                    <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="description"
                                       className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                    Description (optionnelle) :
                                </label>
                                <textarea
                                    id="description"
                                    {...register("description")}
                                    rows="3"
                                    placeholder="Description du produit"
                                    className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                />
                            </div>

                            {/* Responsive grid for price and barcode */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div>
                                    <label htmlFor="price"
                                           className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                        Prix :
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        id="price"
                                        {...register("price", {
                                            required: "Le prix est obligatoire.",
                                            min: {value: 0, message: "Le prix doit être positif"}
                                        })}
                                        placeholder="Prix*"
                                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                            errors.price
                                                ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                                : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                        }`}
                                    />
                                    {errors.price && (
                                        <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.price.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="barcode"
                                           className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                        Code à barres :
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            id="barcode"
                                            {...register("barcode")}
                                            placeholder="Code à barres"
                                            className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                        />
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const barcode = await generateProductBarcode();
                                                    setValue("barcode", barcode, {shouldDirty: true});
                                                } catch (e) {
                                                    console.error("Erreur lors de la génération du code-barres", e);
                                                }
                                            }}
                                            title="Générer un code à barres"
                                            className="flex items-center gap-1.5 px-3 py-2 sm:py-2.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                                        >
                                            <RefreshCw size={16}/>
                                            <span className="hidden sm:inline">Générer</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Stock minimum field */}
                            <div>
                                <label htmlFor="minStock"
                                       className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                    Stock minimum :
                                </label>
                                <input
                                    type="number"
                                    id="minStock"
                                    {...register("minStock", {
                                        min: {value: 0, message: "Le stock minimum doit être positif ou nul"}
                                    })}
                                    placeholder="Stock minimum"
                                    defaultValue={0}
                                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                        errors.minStock
                                            ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                            : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                                    }`}
                                />
                                {errors.minStock && (
                                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.minStock.message}</p>
                                )}
                            </div>

                            {/* Reference selectors — Category & Brand */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <RefSelector
                                    label="Catégorie"
                                    required
                                    value={selectedCategory}
                                    placeholder="Sélectionner une catégorie"
                                    onClick={() => setActivePicker('category')}
                                    onClear={() => setSelectedCategory(null)}
                                />
                                <RefSelector
                                    label="Marque"
                                    value={selectedBrand}
                                    placeholder="Sélectionner une marque"
                                    onClick={() => setActivePicker('brand')}
                                    onClear={() => setSelectedBrand(null)}
                                />
                            </div>

                            {/* Reference selectors — Unit & Tax */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <RefSelector
                                    label="Unité"
                                    required
                                    value={selectedUnit ? {...selectedUnit, displayLabel: `${selectedUnit.name} (${selectedUnit.code || ''})`, hideColor: true} : null}
                                    placeholder="Sélectionner une unité"
                                    onClick={() => setActivePicker('unit')}
                                    onClear={() => setSelectedUnit(null)}
                                />
                                <RefSelector
                                    label="Taux de TVA"
                                    value={selectedTax ? {...selectedTax, displayLabel: `${selectedTax.value}% ${selectedTax.name ? `(${selectedTax.name})` : ''}`, hideColor: true} : null}
                                    placeholder="Sélectionner un taux de TVA"
                                    onClick={() => setActivePicker('tax')}
                                    onClear={() => setSelectedTax(null)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Responsive switches */}
                    <div
                        className={`grid grid-cols-1 ${isRestaurant ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-3 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700`}>
                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Actif :</span>
                            <IOSSwitch checked={isActive} onChange={toggleActive}/>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stockable :</span>
                            <IOSSwitch checked={isStockable} onChange={toggleStockable}/>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Vendable :</span>
                            <IOSSwitch checked={isVendable} onChange={toggleVendable}/>
                        </div>
                        {isRestaurant && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ingrédient :</span>
                                <IOSSwitch checked={isIngredient} onChange={toggleIngredient}/>
                            </div>
                        )}
                    </div>

                    {/* Calendrier : visibilité sur le calendrier / la réservation en ligne + durée (salon uniquement) */}
                    {isBeautySalon && (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <span className="flex items-start gap-2">
                                    <Calendar className="w-4 h-4 mt-0.5 text-primary flex-shrink-0"/>
                                    <span>
                                        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Afficher dans le calendrier</span>
                                        <span className="block text-xs text-gray-400 mt-0.5">Proposé au calendrier et à la réservation en ligne</span>
                                    </span>
                                </span>
                                <IOSSwitch
                                    checked={showOnCalendar}
                                    onChange={() => setShowOnCalendar(prev => !prev)}
                                />
                            </div>

                            {showOnCalendar && (
                                <div className="flex items-center gap-2 pl-3 sm:ml-6 border-l-2 border-primary/30">
                                    <Clock className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                                    <label htmlFor="product-duration" className="text-sm text-gray-700 dark:text-gray-300">Durée</label>
                                    <input
                                        id="product-duration"
                                        type="number"
                                        value={duration}
                                        onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                                        min="5"
                                        step="5"
                                        className="w-20 px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                    />
                                    <span className="text-sm text-gray-400">minutes</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Ingrédient — code interne + prix d'achat HT/TTC (restaurant uniquement) */}
                    {isRestaurant && isIngredient && (
                        <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                            <div>
                                <label htmlFor="internalCode"
                                       className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                    Code interne :
                                </label>
                                <input
                                    type="text"
                                    id="internalCode"
                                    {...register("internalCode")}
                                    placeholder="Ex: MOZ-001"
                                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div>
                                    <label htmlFor="purchasePriceHt"
                                           className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                        Prix d'achat HT :
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        id="purchasePriceHt"
                                        value={purchasePriceHt}
                                        onChange={(e) => handlePurchaseHtChange(e.target.value)}
                                        placeholder="Ex: 66.67"
                                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="purchasePriceTtc"
                                           className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                        Prix d'achat TTC :
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        id="purchasePriceTtc"
                                        value={purchasePriceTtc}
                                        onChange={(e) => handlePurchaseTtcChange(e.target.value)}
                                        placeholder="Ex: 80.00"
                                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>
                            </div>
                            {!selectedTax && (
                                <p className="text-xs text-orange-500">
                                    Sélectionnez un taux de TVA ci-dessus pour calculer automatiquement le HT/TTC.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </form>

            {/* Color palette overlay - responsive & scrollable */}
            {showPalette && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={togglePalette}/>
                    <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
                        <div className="relative w-full sm:max-w-2xl bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col h-[85vh] sm:h-auto sm:max-h-[85vh]"
                             onClick={e => e.stopPropagation()}>
                            <ColorPalette
                                initialColor={currentColor}
                                onClose={togglePalette}
                                onColorChange={handleColorChange}
                                onImageChange={handleImageChange}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Reference Picker Modals */}
            {activePicker === 'category' && (
                <ReferencePickerModal
                    config={categoriesConfig}
                    currentValue={selectedCategory?.id}
                    onSelect={(item) => { setSelectedCategory(item); setActivePicker(null); }}
                    onClose={() => setActivePicker(null)}
                />
            )}
            {activePicker === 'brand' && (
                <ReferencePickerModal
                    config={brandsConfig}
                    currentValue={selectedBrand?.id}
                    onSelect={(item) => { setSelectedBrand(item); setActivePicker(null); }}
                    onClose={() => setActivePicker(null)}
                />
            )}
            {activePicker === 'unit' && (
                <ReferencePickerModal
                    config={unitsConfig}
                    currentValue={selectedUnit?.id}
                    onSelect={(item) => { setSelectedUnit(item); setActivePicker(null); }}
                    onClose={() => setActivePicker(null)}
                    displayValue={(item) => `${item.name} (${item.code || ''})`}
                />
            )}
            {activePicker === 'tax' && (
                <ReferencePickerModal
                    config={taxesConfig}
                    currentValue={selectedTax?.id}
                    onSelect={(item) => { setSelectedTax(item); setActivePicker(null); }}
                    onClose={() => setActivePicker(null)}
                    displayValue={(item) => `${item.value}% ${item.name ? `(${item.name})` : ''}`}
                />
            )}

            {/* Responsive Notification Modal */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />

            {/* Add required CSS for animations */}
            <style jsx>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .selection-bar-appear {
                    animation: slideDown 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default NewProductForm;
